if (!customElements.get('blur-banner')) {
  customElements.define('blur-banner',
    class extends HTMLElement {
      constructor() {
        super();
        this.hero = null;
        this.contentBottom = null;
        this.contentBottomContent = null;
        this.contentTop = null;
        this.container = null;

        this.config = {
          windowHeight: window.innerHeight
        }

        this.rafId = null;
        this.isScrollHandlerScheduled = false;
      }

      connectedCallback() {
        this.hero = this.querySelector('.wt-blur-banner__hero');
        this.contentBottom = this.querySelector('.wt-blur-banner__content__bottom');
        this.contentBottomContent = this.contentBottom?.querySelector('.wt-blur-banner__content__bottom__content');
        this.contentTop = this.querySelector('.wt-blur-banner__content__top');
        this.container = this.querySelector('.wt-blur-banner__container');
        if (!this.hero || !this.contentBottom || !this.contentBottomContent) {
          return;
        }

        this.storyShift = 0;
        this.updateConfig();
        this.resizeHandler = () => { this.updateConfig(); this.handleScroll(); };
        this.scrollHandler = () => this.handleScroll();
        window.addEventListener('resize', this.resizeHandler);
        window.addEventListener('scroll', this.scrollHandler, { passive: true });
        this.handleScroll();
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.resizeHandler);
        window.removeEventListener('scroll', this.scrollHandler);
        if (this.rafId !== null) {
          cancelAnimationFrame(this.rafId);
          this.rafId = null;
        }
        this.isScrollHandlerScheduled = false;
      }

      handleScroll() {
        if (!this.isScrollHandlerScheduled) {
          this.isScrollHandlerScheduled = true;
          this.rafId = requestAnimationFrame(() => {
            this.updateScrollEffects();
            this.isScrollHandlerScheduled = false;
            this.rafId = null;
          });
        }
      }

      updateConfig() {
        this.config = {
          windowHeight: window.innerHeight
        }
      }

      // Scroll-linked, paint-only effects. All reads happen before all writes (one rAF).
      updateScrollEffects() {
        if (!this.hero || !this.contentBottomContent || !this.container) {
          return;
        }

        const vh = this.config.windowHeight;
        const containerRect = this.container.getBoundingClientRect();
        const scrolled = Math.max(0, -containerRect.top);
        const pinScroll = Math.max(1, containerRect.height - vh);

        // Overlay darkening follows the section's scroll progress (0 at entry, 1 when
        // the pinned image releases) instead of popping via a class.
        const progress = Math.min(1, scrolled / pinScroll);
        this.style.setProperty('--lux-blur-progress', progress.toFixed(3));

        // Class stays for the blur-enabled variant (backdrop-filter cannot be scrubbed cheaply).
        if (progress > 0) {
          this.hero.classList.add('wt-blur-banner__hero--filter-active');
        } else {
          this.hero.classList.remove('wt-blur-banner__hero--filter-active');
        }

        // Story choreography (consumed only where CSS opts in, e.g. the homepage
        // installments story on desktop). Tsoulfa's logic: the cards stick to the BOTTOM
        // of the pinned image and the intro travels down toward them on scroll. Native
        // sticky cannot pin the cards (the vendor entrance animation leaves an identity
        // transform on the section root, hijacking the containing block), so both pins
        // are JS-computed and applied transform-only; CSS gates them behind width +
        // reduced-motion media queries. All reads happen before all writes.
        if (this.contentTop && this.contentBottom) {
          const introRect = this.contentTop.getBoundingClientRect();
          const cardsRect = this.contentBottom.getBoundingClientRect();
          const introTopBase = introRect.top - this.storyShift;

          // Cards: positioned by NATIVE sticky (CSS), never by JS - a per-frame JS
          // transform corrects one frame behind compositor scrolling and reads as
          // bouncing. JS only flips their visibility: held invisible during the
          // image's arrival, fading in (opacity-only) once the image fills the screen.
          const parked = containerRect.top <= 0 && containerRect.bottom > 0;
          this.style.setProperty('--lux-cards-vis', parked ? '1' : '0');

          // Intro: descends at 45% of scroll speed, stopping 32px above the boxes'
          // sticky-rendered position. The meet target may be negative (short
          // viewports, and the release phase): the intro then rises just enough to
          // keep hugging the boxes, rate-limited to the scroll itself (never faster
          // than -scrolled, so engagement can never snap it).
          const meetTarget = cardsRect.top - 32 - (introTopBase + introRect.height);
          let shift = Math.min(scrolled * 0.45, meetTarget);
          shift = Math.max(shift, -scrolled);
          if (Math.abs(shift - this.storyShift) > 0.5) {
            this.storyShift = shift;
            this.style.setProperty('--lux-story-shift', shift.toFixed(1) + 'px');
          }
        }
      }
    }
  );
}
